import {
  EdiClaimType,
  IEdiUploadMapping,
} from "../../../types/ediConversionFile";
import { IProvider, SpecialtyList } from "../../../types/Provider/Provider";

// Mirrors the backend's validateMappingSet (CLE-2424). INSTITUTIONAL and PROFESSIONAL_ANESTHESIA
// may each be mapped to a single provider; PROFESSIONAL_PROVIDER may be mapped to more than one
// provider, but only to split by place of service — every such rule must select a non-empty,
// pairwise-disjoint place of service so each claim routes to exactly one provider (no double
// import). Place-of-service comparison uses the API's raw category values (e.g. "Inpatient",
// "Non-Facility"), matching how the modal stores them. Returns a user-facing error string, or null
// when the mapping set is valid.
export const validateEdiMappingSet = (
  mappings: IEdiUploadMapping[]
): string | null => {
  const counts: Record<string, number> = {};
  mappings.forEach((m) => {
    counts[m.claimType] = (counts[m.claimType] ?? 0) + 1;
  });

  if ((counts.INSTITUTIONAL ?? 0) > 1) {
    return "Institutional claims can map to only one provider.";
  }
  if ((counts.PROFESSIONAL_ANESTHESIA ?? 0) > 1) {
    return "Anesthesia claims can map to only one provider.";
  }

  const professional = mappings.filter(
    (m) => m.claimType === "PROFESSIONAL_PROVIDER"
  );
  if (professional.length > 1) {
    const seen = new Set<string>();
    for (let i = 0; i < professional.length; i += 1) {
      const pos = professional[i].placeOfServiceFilter ?? [];
      if (pos.length === 0) {
        return "When routing professional claims to more than one provider, set a place of service (Facility or Non-Facility) on each rule.";
      }
      for (let j = 0; j < pos.length; j += 1) {
        if (seen.has(pos[j])) {
          return "Each place of service can map to only one provider — adjust the Facility / Non-Facility selections.";
        }
        seen.add(pos[j]);
      }
    }
  }

  return null;
};

// Provider specialties a rule of each claim type accepts. Mirrors the backend's validateUploadMappings
// (services/clear-api/src/domains/edi_conversion/edi_mapping_validation.ts): institutional needs a
// FACILITY provider, anesthesia an ANESTHESIOLOGIST or FACILITY one, and the generic provider rule
// takes anything except ANESTHESIOLOGIST.
export const allowedSpecialtiesForClaimType = (
  claimType: EdiClaimType
): SpecialtyList[] => {
  if (claimType === "INSTITUTIONAL") return [SpecialtyList.FACILITY];
  if (claimType === "PROFESSIONAL_ANESTHESIA")
    return [SpecialtyList.FACILITY, SpecialtyList.ANESTHESIOLOGIST];
  return [
    SpecialtyList.FACILITY,
    SpecialtyList.PATHOLOGIST,
    SpecialtyList.PRIMARY_PROVIDER,
    SpecialtyList.ASSISTANT_PROVIDER,
  ];
};

// The specialty check the server runs on every rule, client-side so a mismatch shows before submit.
// Worded like the server's message. Returns null when the provider fits the rule, or when the provider
// is not in the list (the server reports a missing provider itself).
export const ediMappingSpecialtyError = (
  mapping: Pick<IEdiUploadMapping, "claimType" | "providerId">,
  providers: Pick<IProvider, "id" | "specialty">[]
): string | null => {
  const provider = providers.find((p) => p.id === mapping.providerId);
  if (!provider) return null;
  if (
    allowedSpecialtiesForClaimType(mapping.claimType).includes(
      provider.specialty
    )
  )
    return null;
  if (mapping.claimType === "PROFESSIONAL_PROVIDER")
    return `Provider for the ${mapping.claimType} mapping must not be an ANESTHESIOLOGIST provider (has ${provider.specialty})`;
  const allowed =
    mapping.claimType === "PROFESSIONAL_ANESTHESIA"
      ? "ANESTHESIOLOGIST or FACILITY"
      : "FACILITY";
  return `Provider for the ${mapping.claimType} mapping must have specialty ${allowed} (has ${provider.specialty})`;
};
